import { formatDurationShort } from "./routeModelsData.js";
import {
  getSameSiteSequenceDisplayDistanceLabel,
  isInitialRouteSequenceTravelSegment,
} from "./fleetTimelinePresentation.js";

function parseSequenceDistanceKm(distanceLabel = "") {
  if (typeof distanceLabel !== "string") {
    return null;
  }

  const match = distanceLabel.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) {
    return null;
  }

  const parsedDistance = Number.parseFloat(match[0].replace(",", "."));
  return Number.isFinite(parsedDistance) ? parsedDistance : null;
}

export function getRouteSequenceGroupStartValue(item = null) {
  const displayStartSequence = Number(item?.sameSiteGroupDisplayStartSequence ?? item?.startSequence);
  if (Number.isInteger(displayStartSequence) && displayStartSequence > 0) {
    return displayStartSequence;
  }

  const displaySequence = Number(item?.routeSequenceDisplaySequence ?? item?.globalSequence);
  return Number.isInteger(displaySequence) && displaySequence > 0 ? displaySequence : 0;
}

export function getRouteSequenceGroupEndValue(item = null) {
  const displayEndSequence = Number(item?.sameSiteGroupDisplayEndSequence ?? item?.endSequence);
  if (Number.isInteger(displayEndSequence) && displayEndSequence > 0) {
    return displayEndSequence;
  }

  const startSequence = getRouteSequenceGroupStartValue(item);
  const groupSize = Number(item?.sameSiteGroupSize) || 1;
  return startSequence > 0 ? startSequence + Math.max(groupSize, 1) - 1 : 0;
}

export function getGroupedSegmentSequenceLabel(groupItem) {
  const startSequence = getRouteSequenceGroupStartValue(groupItem);
  const endSequence = getRouteSequenceGroupEndValue(groupItem) || startSequence;

  if (!startSequence) {
    return "";
  }

  return startSequence === endSequence ? String(startSequence) : `${startSequence}-${endSequence}`;
}

export function formatSequenceGroupDistanceLabel(memberSegments = [], displayOverrides = null) {
  const parsedDistances = memberSegments
    .map((segment, memberIndex) => parseSequenceDistanceKm(getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, memberIndex)))
    .filter((distanceKm) => distanceKm != null);

  if (!parsedDistances.length) {
    return "";
  }

  const totalDistanceKm = parsedDistances.reduce((total, distanceKm) => total + distanceKm, 0);
  return `${Math.round(totalDistanceKm)} km`;
}

function normalizeRouteSequenceDurationMinutes(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
}

function compactRouteSequenceDurationLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  return label.replace(/(\d+\s*h\s+\d+)\s*min\b/giu, "$1").trim();
}

export function formatRouteSequenceDurationChipLabel(value, { approximate = false } = {}) {
  const durationMinutes = normalizeRouteSequenceDurationMinutes(value);
  if (durationMinutes == null) {
    return "—";
  }

  if (durationMinutes === 0) {
    return "0 min";
  }

  const formattedLabel = compactRouteSequenceDurationLabel(formatDurationShort(durationMinutes) || "0 min");
  return approximate ? `≈ ${formattedLabel}` : formattedLabel;
}

export function formatRouteSequenceTravelDurationLabel(value) {
  return formatRouteSequenceDurationChipLabel(value, { approximate: true });
}

export function resolveRouteSequenceDisplayedTravelLabel(usesHereTraffic = false) {
  return usesHereTraffic ? "Temps HERE avec trafic" : "Durée affichée";
}

export function buildRouteSequenceDisplayedTravelTitle(displayLabel, usesHereTraffic = false) {
  return usesHereTraffic
    ? `Temps HERE avec trafic : ${displayLabel}`
    : `Temps de conduite affiché : ${displayLabel}`;
}

export function enrichRouteSequenceDurationChipMetadata(
  segment,
  displayDistanceLabel,
  displayTravelDurationMinutes,
  sequenceItems = [],
  { showsHereTrafficDuration = false } = {},
) {
  const displayLabel = formatRouteSequenceTravelDurationLabel(displayTravelDurationMinutes);

  return {
    hoverCardDetails: buildRouteSequenceDurationHoverCard(
      segment,
      displayDistanceLabel,
      displayTravelDurationMinutes,
      sequenceItems,
      { showsHereTrafficDuration },
    ),
    hoverCardTitle: resolveRouteSequenceDisplayedTravelLabel(showsHereTrafficDuration),
    title: buildRouteSequenceDisplayedTravelTitle(displayLabel, showsHereTrafficDuration),
  };
}

export function formatRouteSequenceHoursIndicatorTime(timeValue = "") {
  const resolvedTime = typeof timeValue === "string" ? timeValue.trim() : "";
  if (!resolvedTime) {
    return "";
  }

  const match = resolvedTime.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return resolvedTime;
  }

  const hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isInteger(hours) || hours < 0 || hours > 23) {
    return resolvedTime;
  }

  return `${hours}:${minutes}`;
}

function buildRouteSequenceHoursIndicatorLabel(prefix, timeValue) {
  const compactTime = formatRouteSequenceHoursIndicatorTime(timeValue);
  if (!compactTime) {
    return "";
  }

  return `${prefix} ${compactTime}`;
}

export function buildRouteSequenceOpeningIndicator(openingTime) {
  const resolvedOpeningTime = typeof openingTime === "string" ? openingTime.trim() : "";
  if (!resolvedOpeningTime) {
    return null;
  }

  return {
    key: `opening:${resolvedOpeningTime}`,
    label: buildRouteSequenceHoursIndicatorLabel("O", resolvedOpeningTime),
    tone: "opening",
    title: `Heure d'ouverture du site : ${resolvedOpeningTime}. Le chargement ne peut pas débuter avant cette heure.`,
  };
}

export function buildRouteSequenceClosingIndicator(closingTime) {
  const resolvedClosingTime = typeof closingTime === "string" ? closingTime.trim() : "";
  if (!resolvedClosingTime) {
    return null;
  }

  return {
    key: `closing:${resolvedClosingTime}`,
    label: buildRouteSequenceHoursIndicatorLabel("F", resolvedClosingTime),
    tone: "closing",
    title: `Heure de fermeture du site : ${resolvedClosingTime}. Le chargement doit commencer au plus tard à cette heure.`,
  };
}

function normalizeRouteSequenceLoadingSiteKey(value = "") {
  return typeof value === "string"
    ? value.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    : "";
}

export function resolveRouteSequenceLoadingSiteKey(segment = null) {
  if (!segment || segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
    return "";
  }

  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  if (stopType !== "loading") {
    return "";
  }

  const rawKey = [
    segment?.loading_client_name,
    segment?.client_name,
    segment?.clientName,
    segment?.toLabel,
    segment?.operationLabel,
    segment?.toAddress,
    segment?.clientId,
  ].find((candidate) => typeof candidate === "string" && candidate.trim());

  return normalizeRouteSequenceLoadingSiteKey(rawKey || "");
}

function resolveRouteSequenceGroupOpeningTime(memberSegments = []) {
  let latestOpeningTime = null;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    if (segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
      return;
    }

    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType !== "loading") {
      return;
    }

    const openingTime = segment?.client_loading_opening_time;
    if (typeof openingTime !== "string" || !openingTime.trim()) {
      return;
    }

    const normalizedOpeningTime = openingTime.trim();
    if (latestOpeningTime === null || normalizedOpeningTime > latestOpeningTime) {
      latestOpeningTime = normalizedOpeningTime;
    }
  });

  return latestOpeningTime;
}

function resolveRouteSequenceGroupClosingTime(memberSegments = []) {
  let earliestClosingTime = null;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    if (segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
      return;
    }

    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType !== "loading") {
      return;
    }

    const closingTime = segment?.client_loading_closing_time;
    if (typeof closingTime !== "string" || !closingTime.trim()) {
      return;
    }

    const normalizedClosingTime = closingTime.trim();
    if (earliestClosingTime === null || normalizedClosingTime < earliestClosingTime) {
      earliestClosingTime = normalizedClosingTime;
    }
  });

  return earliestClosingTime;
}

function resolveRouteSequenceGroupLoadingSiteKey(memberSegments = []) {
  const loadingSegment = (Array.isArray(memberSegments) ? memberSegments : []).find(
    (segment) => segment?.stopType === "loading" && !segment?.isPreloadedPreviousDay && !segment?.is_preloaded_previous_day,
  );

  return resolveRouteSequenceLoadingSiteKey(loadingSegment || memberSegments[0] || null);
}

export function isRouteSequenceOpeningIndicator(indicator = null) {
  return indicator?.tone === "opening"
    || (typeof indicator?.key === "string" && indicator.key.startsWith("opening:"));
}

export function isRouteSequenceClosingIndicator(indicator = null) {
  return indicator?.tone === "closing"
    || (typeof indicator?.key === "string" && indicator.key.startsWith("closing:"));
}

export function filterRouteSequenceOpeningIndicators(indicators = [], showOpeningTime = true) {
  if (showOpeningTime !== false) {
    return Array.isArray(indicators) ? indicators.filter(Boolean) : [];
  }

  return (Array.isArray(indicators) ? indicators : []).filter((indicator) => !isRouteSequenceOpeningIndicator(indicator));
}

export function filterRouteSequenceClosingIndicators(indicators = [], showClosingTime = true) {
  if (showClosingTime !== false) {
    return Array.isArray(indicators) ? indicators.filter(Boolean) : [];
  }

  return (Array.isArray(indicators) ? indicators : []).filter((indicator) => !isRouteSequenceClosingIndicator(indicator));
}

export function buildRouteSequenceOpeningVisibilityByItemKey(renderItems = []) {
  const seenSiteKeys = new Set();
  const visibilityByKey = new Map();

  (Array.isArray(renderItems) ? renderItems : []).forEach((item) => {
    if (!item?.key) {
      return;
    }

    if (item.kind === "group-member") {
      visibilityByKey.set(item.key, false);
      return;
    }

    if (item.kind === "group") {
      const memberSegments = Array.isArray(item.memberSegments) ? item.memberSegments : [];
      const openingTime = resolveRouteSequenceGroupOpeningTime(memberSegments);
      const siteKey = resolveRouteSequenceGroupLoadingSiteKey(memberSegments);
      const shouldShow = Boolean(openingTime && siteKey && !seenSiteKeys.has(siteKey));

      if (shouldShow) {
        seenSiteKeys.add(siteKey);
      }

      visibilityByKey.set(item.key, shouldShow);
      return;
    }

    const segment = item.segment || null;
    const openingTime = typeof segment?.client_loading_opening_time === "string"
      ? segment.client_loading_opening_time.trim()
      : "";
    const siteKey = resolveRouteSequenceLoadingSiteKey(segment);
    const shouldShow = Boolean(openingTime && (!siteKey || !seenSiteKeys.has(siteKey)));

    if (shouldShow && siteKey) {
      seenSiteKeys.add(siteKey);
    }

    visibilityByKey.set(item.key, shouldShow);
  });

  return visibilityByKey;
}

export function shouldDisplayRouteSequenceOpeningForItem(itemKey = "", visibilityByKey = null) {
  if (!itemKey || !(visibilityByKey instanceof Map) || !visibilityByKey.has(itemKey)) {
    return true;
  }

  return visibilityByKey.get(itemKey) === true;
}

export function shouldDisplayRouteSequenceOpeningForSegment(segment = null, renderItems = [], visibilityByKey = null) {
  if (!segment || !(visibilityByKey instanceof Map)) {
    return true;
  }

  const directItem = (Array.isArray(renderItems) ? renderItems : []).find((item) => (
    (item?.kind === "segment" || item?.kind === "group-member")
    && item?.segment?.tripId === segment?.tripId
    && item?.segment?.globalSequence === segment?.globalSequence
  ));

  if (directItem) {
    return shouldDisplayRouteSequenceOpeningForItem(directItem.key, visibilityByKey);
  }

  const parentGroup = (Array.isArray(renderItems) ? renderItems : []).find((item) => (
    item?.kind === "group"
    && (Array.isArray(item.memberSegments) ? item.memberSegments : []).some((memberSegment) => (
      memberSegment?.tripId === segment?.tripId
      && memberSegment?.globalSequence === segment?.globalSequence
    ))
  ));

  if (parentGroup && parentGroup.isExpanded !== true) {
    return shouldDisplayRouteSequenceOpeningForItem(parentGroup.key, visibilityByKey);
  }

  return true;
}

export function buildRouteSequenceClosingVisibilityByItemKey(renderItems = []) {
  const seenSiteKeys = new Set();
  const visibilityByKey = new Map();

  (Array.isArray(renderItems) ? renderItems : []).forEach((item) => {
    if (!item?.key) {
      return;
    }

    if (item.kind === "group-member") {
      visibilityByKey.set(item.key, false);
      return;
    }

    if (item.kind === "group") {
      const memberSegments = Array.isArray(item.memberSegments) ? item.memberSegments : [];
      const closingTime = resolveRouteSequenceGroupClosingTime(memberSegments);
      const siteKey = resolveRouteSequenceGroupLoadingSiteKey(memberSegments);
      const isRepeatVisit = Boolean(siteKey && seenSiteKeys.has(siteKey));
      const shouldShow = Boolean(closingTime && siteKey && isRepeatVisit);

      if (siteKey && !seenSiteKeys.has(siteKey)) {
        seenSiteKeys.add(siteKey);
      }

      visibilityByKey.set(item.key, shouldShow);
      return;
    }

    const segment = item.segment || null;
    const closingTime = typeof segment?.client_loading_closing_time === "string"
      ? segment.client_loading_closing_time.trim()
      : "";
    const siteKey = resolveRouteSequenceLoadingSiteKey(segment);
    const isRepeatVisit = Boolean(siteKey && seenSiteKeys.has(siteKey));
    const shouldShow = Boolean(closingTime && siteKey && isRepeatVisit);

    if (siteKey && !seenSiteKeys.has(siteKey)) {
      seenSiteKeys.add(siteKey);
    }

    visibilityByKey.set(item.key, shouldShow);
  });

  return visibilityByKey;
}

export function shouldDisplayRouteSequenceClosingForItem(itemKey = "", visibilityByKey = null) {
  if (!itemKey || !(visibilityByKey instanceof Map) || !visibilityByKey.has(itemKey)) {
    return false;
  }

  return visibilityByKey.get(itemKey) === true;
}

export function shouldDisplayRouteSequenceClosingForSegment(segment = null, renderItems = [], visibilityByKey = null) {
  if (!segment || !(visibilityByKey instanceof Map)) {
    return false;
  }

  const directItem = (Array.isArray(renderItems) ? renderItems : []).find((item) => (
    (item?.kind === "segment" || item?.kind === "group-member")
    && item?.segment?.tripId === segment?.tripId
    && item?.segment?.globalSequence === segment?.globalSequence
  ));

  if (directItem) {
    return shouldDisplayRouteSequenceClosingForItem(directItem.key, visibilityByKey);
  }

  const parentGroup = (Array.isArray(renderItems) ? renderItems : []).find((item) => (
    item?.kind === "group"
    && (Array.isArray(item.memberSegments) ? item.memberSegments : []).some((memberSegment) => (
      memberSegment?.tripId === segment?.tripId
      && memberSegment?.globalSequence === segment?.globalSequence
    ))
  ));

  if (parentGroup && parentGroup.isExpanded !== true) {
    return shouldDisplayRouteSequenceClosingForItem(parentGroup.key, visibilityByKey);
  }

  return false;
}

function getRouteDistanceHoverSourceLabel(source = "", sourceLabel = "") {
  const trimmedSourceLabel = typeof sourceLabel === "string" ? sourceLabel.trim() : "";
  if (trimmedSourceLabel) {
    return trimmedSourceLabel;
  }

  if (source === "roadapi") {
    return "HERE Routing (mode camion)";
  }
  if (source === "geometrymeasured") {
    return "Mesuré sur la géométrie HERE";
  }
  if (source === "haversineestimate") {
    return "Estimé (haversine)";
  }

  return "Indisponible";
}

function getRouteDistanceSourceExplanation(source = "") {
  if (source === "roadapi") {
    return "La distance affichée vient du routage HERE en mode camion.";
  }
  if (source === "geometrymeasured") {
    return "La distance affichée est mesurée localement à partir de la géométrie renvoyée par HERE.";
  }
  if (source === "haversineestimate") {
    return "La distance affichée est une estimation haversine faute de longueur routière exploitable.";
  }

  return "Aucune source de distance qualifiée n'est disponible pour cette ligne.";
}

function formatRouteSequenceHereTimeValue(durationMinutes, { usesRealRoute = false } = {}) {
  const durationLabel = formatDurationShort(durationMinutes) || "0 min";
  return usesRealRoute
    ? `${durationLabel} · trajet routier (non haversine)`
    : durationLabel;
}

function formatRouteSequenceTruckDurationCalculationValue(distanceLabel, roundedSpeedKmh, durationMinutes) {
  if (roundedSpeedKmh == null) {
    return null;
  }

  const resolvedDistanceLabel = typeof distanceLabel === "string" && distanceLabel.trim()
    ? distanceLabel.trim()
    : "distance affichée";
  return `${resolvedDistanceLabel} ÷ ${roundedSpeedKmh} km/h = ${formatRouteSequenceTravelDurationLabel(durationMinutes)}`;
}

function formatGroupedRouteSequenceTruckDurationCalculationValue(distanceLabel, speeds = [], durationMinutes) {
  if (speeds.length === 1) {
    return formatRouteSequenceTruckDurationCalculationValue(distanceLabel, speeds[0], durationMinutes);
  }

  if (speeds.length > 1) {
    return `Somme des segments avec leurs vitesses camion = ${formatRouteSequenceTravelDurationLabel(durationMinutes)}`;
  }

  return null;
}

function formatRouteSequenceSpeedReferenceValue(roundedSpeedKmh) {
  if (roundedSpeedKmh != null) {
    return `${roundedSpeedKmh} km/h · table Camion`;
  }

  return "Table Camion";
}

function buildRouteSequenceSpeedReferenceNote() {
  return "Durée affichée = distance ÷ vitesse camion.";
}

function getSameSiteSequenceRangeLabel(item) {
  const startSequence = getRouteSequenceGroupStartValue(item);
  const groupSize = Number(item?.sameSiteGroupSize) || 1;
  const endSequence = getRouteSequenceGroupEndValue(item) || (startSequence > 0 ? startSequence + groupSize - 1 : 0);

  if (!startSequence || groupSize <= 1) {
    return "";
  }

  return startSequence === endSequence
    ? `la séquence ${startSequence}`
    : `les séquences ${startSequence} à ${endSequence}`;
}

function getRouteSequenceMemberSegments(item, sequenceItems = []) {
  const items = Array.isArray(sequenceItems) ? sequenceItems.filter(Boolean) : [];
  if (!item?.isSameSiteSequenceGroup || !item?.sameSiteGroupKey) {
    return item ? [item] : [];
  }

  const memberSegments = items.filter((candidate) => candidate?.sameSiteGroupKey === item.sameSiteGroupKey);
  return memberSegments.length ? memberSegments : (item ? [item] : []);
}

function sumRouteSequenceDurationMinutes(items = [], readValue) {
  let totalMinutes = 0;
  let hasValue = false;

  items.forEach((item) => {
    const durationMinutes = normalizeRouteSequenceDurationMinutes(
      typeof readValue === "function" ? readValue(item) : null,
    );
    if (durationMinutes == null) {
      return;
    }

    totalMinutes += durationMinutes;
    hasValue = true;
  });

  return hasValue ? totalMinutes : null;
}

export function buildRouteSequenceDistanceHoverCard(item, displayDistanceLabel, rawDistanceLabel, sequenceItems = [], hovercardContext = null) {
  const memberSegments = getRouteSequenceMemberSegments(item, sequenceItems);
  const isSameSiteLead = memberSegments.length > 1 && Number(item?.sameSiteGroupIndex) === 0;
  const isSameSiteFollower = memberSegments.length > 1 && Number(item?.sameSiteGroupIndex) > 0;
  const groupedDistanceLabel = memberSegments.length > 1
    ? formatSequenceGroupDistanceLabel(memberSegments, null)
    : rawDistanceLabel;
  const source = typeof item?.routeDistanceSource === "string"
    ? item.routeDistanceSource.trim().toLowerCase()
    : "";
  const sourceLabel = getRouteDistanceHoverSourceLabel(source, item?.routeDistanceSourceLabel || "");
  const noteParts = [
    getRouteDistanceSourceExplanation(source),
    hovercardContext?.here_time_aware_routing_enabled === true
      ? "Quand le trafic HERE est actif, cette distance reste inchangée; seul le temps affiché bascule vers la durée HERE avec trafic lorsqu'elle est disponible."
      : "La vitesse du camion ne change pas cette distance. Elle sert seulement à calculer la durée.",
  ];

  if (isSameSiteLead) {
    noteParts.push(`La distance affichée regroupe ${getSameSiteSequenceRangeLabel(item) || "les segments consécutifs du même site"} sur la première ligne du site.`);
  } else if (isSameSiteFollower) {
    noteParts.push("Sur cette ligne, la distance est reportée sur la première ligne du même site pour éviter un doublon visuel.");
  }

  return {
    type: "formula",
    lines: [
      {
        label: "Distance affichée",
        value: displayDistanceLabel || "—",
      },
      {
        label: "Source",
        value: sourceLabel,
      },
      isSameSiteFollower && groupedDistanceLabel
        ? {
            label: "Distance regroupée",
            value: groupedDistanceLabel,
          }
        : null,
      !isSameSiteFollower && rawDistanceLabel && rawDistanceLabel !== displayDistanceLabel
        ? {
            label: "Segment brut",
            value: rawDistanceLabel,
          }
        : null,
    ].filter(Boolean),
    note: noteParts.join(" "),
    total: displayDistanceLabel || "—",
    totalLabel: "Distance affichée",
  };
}

export function buildRouteSequenceDurationHoverCard(
  item,
  displayDistanceLabel,
  displayTravelDurationMinutes,
  sequenceItems = [],
  { showsHereTrafficDuration = false } = {},
) {
  const memberSegments = getRouteSequenceMemberSegments(item, sequenceItems);
  const isSameSiteLead = memberSegments.length > 1 && Number(item?.sameSiteGroupIndex) === 0;
  const isSameSiteFollower = memberSegments.length > 1 && Number(item?.sameSiteGroupIndex) > 0;
  const source = typeof item?.routeDistanceSource === "string"
    ? item.routeDistanceSource.trim().toLowerCase()
    : "";
  const sourceLabel = getRouteDistanceHoverSourceLabel(source, item?.routeDistanceSourceLabel || "");
  const speedKmh = Number(item?.routeSpeedKmh);
  const roundedSpeedKmh = Number.isFinite(speedKmh) && speedKmh > 0 ? Math.round(speedKmh) : null;
  const serviceDurationMinutes = normalizeRouteSequenceDurationMinutes(item?.serviceDurationMinutes);
  const truckDurationCalculationValue = formatRouteSequenceTruckDurationCalculationValue(
    displayDistanceLabel || item?.distanceLabel || "",
    roundedSpeedKmh,
    displayTravelDurationMinutes,
  );
  const noteParts = showsHereTrafficDuration
    ? [
        "Durée affichée = temps HERE avec trafic.",
        roundedSpeedKmh != null
          ? "La vitesse camion reste affichée en référence."
          : "La vitesse camion ne sert pas au temps affiché.",
      ]
    : [buildRouteSequenceSpeedReferenceNote()];

  if (isSameSiteLead) {
    noteParts.push(`Les minutes de ${getSameSiteSequenceRangeLabel(item) || "ce groupe same-site"} sont regroupées sur la première ligne du site.`);
  } else if (isSameSiteFollower) {
    noteParts.push("Sur cette ligne, les minutes sont reportées sur la première ligne du même site pour éviter un doublon visuel.");
  }

  if (isInitialRouteSequenceTravelSegment(item) && serviceDurationMinutes != null && serviceDurationMinutes > 0) {
    noteParts.push(
      showsHereTrafficDuration
        ? `L'étape Inspection inclut aussi ${formatDurationShort(serviceDurationMinutes) || "0 min"} d'inspection camion au dépôt, mais le temps HERE affiché reste calculé hors inspection.`
        : `L'étape Inspection inclut aussi ${formatDurationShort(serviceDurationMinutes) || "0 min"} d'inspection camion au dépôt, mais la durée de conduite affichée reste calculée hors inspection.`,
    );
  }

  return {
    type: "formula",
    lines: [
      {
        label: "Distance affichée",
        value: displayDistanceLabel || item?.distanceLabel || "—",
      },
      {
        label: "Source dist.",
        value: sourceLabel,
      },
      showsHereTrafficDuration
        ? {
            label: "Temps HERE avec trafic",
            value: formatRouteSequenceHereTimeValue(displayTravelDurationMinutes, { usesRealRoute: true }),
          }
        : truckDurationCalculationValue
          ? {
              label: "Calcul camion",
              value: truckDurationCalculationValue,
            }
          : null,
      !showsHereTrafficDuration
        ? {
            label: "Vitesse camion",
            value: formatRouteSequenceSpeedReferenceValue(roundedSpeedKmh),
          }
        : roundedSpeedKmh != null
          ? {
              label: "Vitesse camion",
              value: formatRouteSequenceSpeedReferenceValue(roundedSpeedKmh),
            }
          : null,
    ].filter(Boolean),
    note: noteParts.join(" "),
    total: formatRouteSequenceTravelDurationLabel(displayTravelDurationMinutes),
    totalLabel: resolveRouteSequenceDisplayedTravelLabel(showsHereTrafficDuration),
  };
}

function getGroupedRouteSequenceDistanceSourceInfo(memberSegments = []) {
  const sourceEntries = Array.from(new Set(
    memberSegments
      .map((segment) => (typeof segment?.routeDistanceSource === "string" ? segment.routeDistanceSource.trim().toLowerCase() : "unavailable"))
      .filter(Boolean),
  ));

  if (sourceEntries.length === 1) {
    const source = sourceEntries[0];
    const matchingSegment = memberSegments.find((segment) => {
      return (typeof segment?.routeDistanceSource === "string" ? segment.routeDistanceSource.trim().toLowerCase() : "unavailable") === source;
    });

    return {
      label: getRouteDistanceHoverSourceLabel(source, matchingSegment?.routeDistanceSourceLabel || ""),
      source,
    };
  }

  return {
    label: sourceEntries.length > 1 ? "Sources mixtes" : "Indisponible",
    source: sourceEntries.length > 1 ? "mixed" : "unavailable",
  };
}

export function buildGroupedRouteSequenceDistanceHoverCard(groupItem, memberSegments, distanceLabel, hovercardContext = null) {
  const sourceInfo = getGroupedRouteSequenceDistanceSourceInfo(memberSegments);
  const noteParts = [
    sourceInfo.source === "mixed"
      ? "La distance affichée regroupe plusieurs segments dont les sources de distance ne sont pas toutes identiques."
      : getRouteDistanceSourceExplanation(sourceInfo.source),
    hovercardContext?.here_time_aware_routing_enabled === true
      ? "Quand le trafic HERE est actif, cette distance regroupée reste inchangée; seul le temps affiché du groupe bascule vers la durée HERE avec trafic lorsqu'elle est disponible."
      : "La vitesse du camion ne change pas cette distance. Elle sert seulement à calculer la durée du groupe.",
    "Cette ligne repliée regroupe les segments consécutifs du même site.",
  ];

  return {
    type: "formula",
    lines: [
      {
        label: "Distance regroupée",
        value: distanceLabel || "—",
      },
      {
        label: "Source",
        value: sourceInfo.label,
      },
      {
        label: "Séquences",
        value: getGroupedSegmentSequenceLabel(groupItem) || `${memberSegments.length}`,
      },
    ],
    note: noteParts.join(" "),
    total: distanceLabel || "—",
    totalLabel: "Distance regroupée",
  };
}

export function buildGroupedRouteSequenceDurationHoverCard(
  groupItem,
  memberSegments,
  distanceLabel,
  totalTravelDurationMinutes,
  { showsHereTrafficDuration = false } = {},
) {
  const sourceInfo = getGroupedRouteSequenceDistanceSourceInfo(memberSegments);
  const speeds = Array.from(new Set(
    memberSegments
      .map((segment) => normalizeRouteSequenceDurationMinutes(segment?.routeSpeedKmh))
      .filter((speedKmh) => speedKmh != null && speedKmh > 0)
      .map((speedKmh) => Math.round(speedKmh)),
  ));
  const speedLabel = speeds.length === 1
    ? `${speeds[0]} km/h`
    : speeds.length > 1
      ? "Plusieurs vitesses camion"
      : "Paramètre camion";
  const truckDurationCalculationValue = formatGroupedRouteSequenceTruckDurationCalculationValue(
    distanceLabel || "",
    speeds,
    totalTravelDurationMinutes,
  );
  const noteParts = showsHereTrafficDuration
    ? [
        "Durée affichée = temps HERE avec trafic.",
        speeds.length === 1
          ? "La vitesse camion reste affichée en référence."
          : "La vitesse camion ne sert pas au temps affiché.",
      ]
    : [buildRouteSequenceSpeedReferenceNote()];

  noteParts.push("Cette ligne repliée regroupe les segments consécutifs du même site.");

  return {
    type: "formula",
    lines: [
      {
        label: "Distance regroupée",
        value: distanceLabel || "—",
      },
      {
        label: "Source dist.",
        value: sourceInfo.label,
      },
      showsHereTrafficDuration
        ? {
            label: "Temps HERE avec trafic",
            value: formatRouteSequenceHereTimeValue(totalTravelDurationMinutes, { usesRealRoute: true }),
          }
        : truckDurationCalculationValue
          ? {
              label: "Calcul camion",
              value: truckDurationCalculationValue,
            }
          : null,
      !showsHereTrafficDuration
        ? {
            label: "Vitesse camion",
            value: speeds.length === 1
              ? formatRouteSequenceSpeedReferenceValue(speeds[0])
              : speedLabel === "Paramètre camion"
                ? formatRouteSequenceSpeedReferenceValue(null)
                : `${speedLabel} · table Camion`,
          }
        : speeds.length === 1
          ? {
              label: "Vitesse camion",
              value: formatRouteSequenceSpeedReferenceValue(speeds[0]),
            }
          : null,
      {
        label: "Séquences",
        value: getGroupedSegmentSequenceLabel(groupItem) || `${memberSegments.length}`,
      },
    ].filter(Boolean),
    note: noteParts.join(" "),
    total: formatRouteSequenceTravelDurationLabel(totalTravelDurationMinutes),
    totalLabel: resolveRouteSequenceDisplayedTravelLabel(showsHereTrafficDuration),
  };
}
